import api from '../api';

/**
 * Servicio API para Declaración de Renta F-22.
 * Todas las operaciones están vinculadas a id_company + año + régimen.
 */

const BASE_URL = '/api/declaracion-renta/renta';

/**
 * Obtiene la renta guardada (propuesto o pagado, según el módulo) para una
 * empresa, año y régimen. 404 si aún no se ha calculado/cargado nada.
 */
export const getRenta = async (idCompany, anioTributario, regimen) => {
  const response = await api.get(`${BASE_URL}/${idCompany}/${anioTributario}/${regimen}`);
  return response.data;
};

/**
 * Obtiene el certificado de renta pagada (el F-22 presentado ante el SII).
 * Es independiente de la declaración calculada: puede existir uno sin el
 * otro. 404 si no hay nada cargado todavía.
 */
export const getRentaPagado = async (idCompany, anioTributario, regimen) => {
  const response = await api.get(`${BASE_URL}/${idCompany}/${anioTributario}/${regimen}/pagado`);
  return response.data;
};

/**
 * "Calcular Renta": envía los inputs crudos del formulario, el backend
 * calcula, guarda y genera el PDF en un solo paso.
 */
export const calcularRenta = async (idCompany, anioTributario, regimen, data) => {
  const response = await api.post(`${BASE_URL}/${idCompany}/${anioTributario}/${regimen}/calcular`, { data });
  return response.data;
};

/**
 * Carga el certificado de renta pagada (el F-22 presentado ante el SII).
 * Responde 409 si ya hay uno cargado: hay que eliminarlo primero.
 */
export const cargarPdfPagado = async (idCompany, anioTributario, regimen, file) => {
  const formData = new FormData();
  formData.append('pdf', file);
  const response = await api.post(
    `${BASE_URL}/${idCompany}/${anioTributario}/${regimen}/pagado-pdf`,
    formData,
    { headers: { 'Content-Type': undefined } }
  );
  return response.data;
};

/**
 * Elimina SOLO el certificado de renta pagada (fila, asientos y PDF), dejando
 * intacta la declaración calculada.
 */
export const eliminarPdfPagado = async (idCompany, anioTributario, regimen) => {
  const response = await api.delete(`${BASE_URL}/${idCompany}/${anioTributario}/${regimen}/pagado`);
  return response.data;
};

/**
 * Rectificar: borra todo lo generado por "Calcular Renta" para esa empresa
 * — filas de propuesto/pagado, asientos contables y PDFs en R2 — dejando el
 * camino libre para recalcular desde cero. Incluye el certificado pagado.
 */
export const rollbackRenta = async (idCompany, anioTributario, regimen) => {
  const response = await api.post(`${BASE_URL}/${idCompany}/${anioTributario}/${regimen}/rollback`);
  return response.data;
};

/**
 * Obtiene el total de ventas netas del año comercial
 * (facturas_venta + boletas_sii + comprobantes).
 * @param {number} idCompany - ID de la empresa
 * @param {number} anioComercial - Año comercial (ej: 2025 para AT 2026)
 */
export const getVentasNetas = async (idCompany, anioComercial) => {
  const response = await api.get(`/api/declaracion-renta/ventas-netas/${idCompany}/${anioComercial}`);
  return response.data;
};

/**
 * Obtiene el total de compras netas del año comercial (facturas_compra).
 * @param {number} idCompany - ID de la empresa
 * @param {number} anioComercial - Año comercial (ej: 2025 para AT 2026)
 */
export const getComprasNetas = async (idCompany, anioComercial) => {
  const response = await api.get(`/api/declaracion-renta/compras-netas/${idCompany}/${anioComercial}`);
  return response.data;
};

export const getArriendosPagados = async (idCompany, anioComercial) => {
  const response = await api.get(`/api/declaracion-renta/arriendos-pagados/${idCompany}/${anioComercial}`);
  return response.data;
};

export const getIDPCAT = async (idCompany, anioTributario) => {
  const response = await api.get(`/api/declaracion-renta/idpc-at/${idCompany}/${anioTributario}`);
  return response.data;
};

export const getPPMPagados = async (idCompany, anioComercial) => {
  const response = await api.get(`/api/declaracion-renta/ppm-pagados/${idCompany}/${anioComercial}`);
  return response.data;
};

export const getRemuneracionesBrutas = async (idCompany, anioComercial) => {
  const response = await api.get(`/api/declaracion-renta/remuneraciones-brutas/${idCompany}/${anioComercial}`);
  return response.data;
};

export const getRemuneracionesAdeudadas = async (idCompany, anioComercial) => {
  const response = await api.get(`/api/declaracion-renta/remuneraciones-adeudadas/${idCompany}/${anioComercial}`);
  return response.data;
};

export const getHonorariosBrutos = async (idCompany, anioComercial) => {
  const response = await api.get(`/api/declaracion-renta/honorarios-brutos/${idCompany}/${anioComercial}`);
  return response.data;
};

export const getHonorariosAdeudados = async (idCompany, anioComercial) => {
  const response = await api.get(`/api/declaracion-renta/honorarios-adeudados/${idCompany}/${anioComercial}`);
  return response.data;
};

export const getGastosGenerales = async (idCompany, anioComercial) => {
  const response = await api.get(`/api/declaracion-renta/gastos-generales/${idCompany}/${anioComercial}`);
  return response.data;
};

